/**
 * ShiftDetails — editable date / shift / weather form below ShiftSummary.
 *
 * Includes a "Get Weather" button that hits the open-meteo API for the
 * Fair Oaks Mall coordinates and writes the result to the desktop store.
 *
 * All inputs read and write directly through the desktop store.
 */
import { useState, useEffect, useRef } from 'react'
import { useDesktopStore } from '../../stores/desktop'
import { fetchCurrentWeather } from './shiftUtils'

export default function ShiftDetails() {
  const { currentDate, shift, weather, personnel, deskOfficer, supervisors, setCurrentDate, setShift, setWeather, setDeskOfficer, setSupervisors } = useDesktopStore()
  const [fetching, setFetching] = useState(false)
  const [supOpen, setSupOpen] = useState(false)
  const supRef = useRef<HTMLDivElement | null>(null)

  // Auto-fetch weather on mount and every 10 minutes
  useEffect(() => {
    async function autoWeather() {
      try {
        const w = await fetchCurrentWeather()
        setWeather(w)
      } catch { /* silent */ }
    }
    autoWeather()
    const interval = setInterval(autoWeather, 10 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Close supervisor dropdown on outside click
  useEffect(() => {
    if (!supOpen) return
    const onDoc = (e: MouseEvent) => {
      if (supRef.current && !supRef.current.contains(e.target as Node)) setSupOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [supOpen])

  // Derive supervisor names + desk officer name (mirrors FOM Shift Details)
  const lastName = (n: string) => {
    if (!n) return ''
    if (n.includes(',')) return n.split(',')[0].trim()
    const parts = n.trim().split(/\s+/)
    return parts[parts.length - 1] ?? ''
  }
  const SUPERVISOR_ROLES = ['Dir/AD/Lt', 'Sergeant', 'Lieutenant', 'Director of Security', 'Assistant Director', 'Developer']

  // On-duty personnel filtered to supervisor roles — the picker source
  const supCandidates = personnel.filter(p => p.name && SUPERVISOR_ROLES.includes(p.role))

  // Display: prefer manual selection (supervisors[]), fall back to all on-duty supervisors
  const effectiveSupervisorNames = supervisors.length > 0
    ? supervisors
    : supCandidates.map(p => p.name)
  const supervisorNames = effectiveSupervisorNames
    .map(n => lastName(n))
    .filter(Boolean)
    .join(' / ') || '—'

  function toggleSupervisor(name: string) {
    const current = supervisors.length > 0 ? supervisors : supCandidates.map(p => p.name)
    const next = current.includes(name)
      ? current.filter(n => n !== name)
      : [...current, name]
    setSupervisors(next)
  }
  const deskOfficerName = (() => {
    if (!deskOfficer) return '—'
    const match = personnel.find(p => p.callSign === deskOfficer || p.name === deskOfficer)
    return lastName(match?.name ?? deskOfficer) || '—'
  })()

  async function getWeather() {
    setFetching(true)
    try {
      const w = await fetchCurrentWeather()
      setWeather(w)
    } catch {
      alert('Could not fetch weather.')
    }
    setFetching(false)
  }

  const inp: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--bg-deep)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 12px',
    color: 'var(--text-primary)',
    fontSize: 13,
    outline: 'none',
    colorScheme: 'dark' as React.CSSProperties['colorScheme'],
  }

  const lbl: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  }

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '16px 20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--accent)',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
          }}
        >
          🕐 Shift Details
        </div>
        <button
          onClick={getWeather}
          disabled={fetching}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '5px 12px',
            fontSize: 11,
            fontWeight: 700,
            cursor: fetching ? 'wait' : 'pointer',
            background: 'rgba(59,130,246,0.12)',
            color: '#60a5fa',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: 8,
            opacity: fetching ? 0.7 : 1,
          }}
        >
          {fetching ? '⌛ Fetching…' : '🌡️ Get Weather'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={lbl}>Date</label>
          <input
            type="date"
            value={currentDate}
            onChange={e => setCurrentDate(e.target.value)}
            style={inp}
          />
        </div>
        <div>
          <label style={lbl}>Shift</label>
          <select
            value={shift}
            onChange={e => setShift(e.target.value)}
            style={{ ...inp, cursor: 'pointer' }}
          >
            <option>Morning</option>
            <option>Evening</option>
            <option>Night</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div ref={supRef} style={{ position: 'relative' }}>
          <label style={lbl}>Supervisor(s)</label>
          <div
            onClick={() => setSupOpen(o => !o)}
            style={{
              ...inp,
              minHeight: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              color: supervisorNames === '—' ? 'var(--text-muted)' : 'var(--text-primary)',
              fontWeight: 600,
              gap: 8,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{supervisorNames}</span>
            <span style={{ fontSize: 10, opacity: 0.7, flexShrink: 0 }}>{supOpen ? '▲' : '▼'}</span>
          </div>
          {supOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 6,
                maxHeight: 220,
                overflowY: 'auto',
                zIndex: 20,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}
            >
              {supCandidates.length === 0 && (
                <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
                  No supervisors on duty
                </div>
              )}
              {supCandidates.map((p, i) => {
                const checked = supervisors.length === 0
                  ? true
                  : supervisors.includes(p.name)
                return (
                  <label
                    key={`${p.callSign || p.name}-${i}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: 'var(--text-primary)',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSupervisor(p.name)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{p.name}{p.callSign ? ` (${p.callSign})` : ''}</span>
                  </label>
                )
              })}
            </div>
          )}
        </div>
        <div>
          <label style={lbl}>Desk Officer</label>
          <select
            value={deskOfficer}
            onChange={e => setDeskOfficer(e.target.value)}
            style={{ ...inp, cursor: 'pointer' }}
          >
            <option value="">Select…</option>
            {personnel
              .filter(p => p.name)
              .map((p, i) => (
                <option key={`${p.callSign || p.name}-${i}`} value={p.name}>
                  {lastName(p.name)}{p.callSign ? ` (${p.callSign})` : ''}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={lbl}>Temperature</label>
          <input
            type="text"
            value={weather.temp}
            onChange={e => setWeather({ ...weather, temp: e.target.value })}
            placeholder="e.g. 72°F"
            style={inp}
          />
        </div>
        <div>
          <label style={lbl}>Conditions</label>
          <input
            type="text"
            value={weather.conditions}
            onChange={e => setWeather({ ...weather, conditions: e.target.value })}
            placeholder="e.g. Sunny, Cloudy"
            style={inp}
          />
        </div>
      </div>
    </div>
  )
}
